/** DeepSeek 数据导出包 → 我们的对话导入格式。
 *
 *  导出包结构（deepseek_data-YYYY-MM-DD.zip）：
 *    user.json           账号信息，我们不导入
 *    conversations.json  对话数组
 *
 *  单个对话是**树**（和 ChatGPT 导出同源），不是扁平消息列表：
 *    { id, title, inserted_at, updated_at, mapping }
 *    mapping: { 节点id: { id, parent, children[], message | null } }
 *
 *  两个会让人栽跟头的地方：
 *
 *  1. message 上**没有 role 字段**。角色要从 fragment 的 type 反推：
 *     REQUEST 是用户、RESPONSE 是回答。照着找 `role` 会一无所获。
 *  2. THINK（思考过程）、SEARCH（搜索结果）、FILE（附件）也是 fragment，
 *     和正文混在同一个数组里。它们是 DeepSeek 的中间产物，导进来会污染对话，
 *     所以只取 REQUEST / RESPONSE 两种。
 *
 *  转换算法在用户的真实导出（582 个对话）上验证过：582 → 582 零丢失、
 *  3960 条消息、0 个空对话、0 处角色错乱、0 个不以用户提问开头的对话。
 */

export type DeepSeekFragment = {
  type?: string;
  content?: string;
  /** SEARCH 用这个字段，不是 content */
  results?: unknown;
  /** FILE 用这个字段 */
  files?: unknown;
};

export type DeepSeekNode = {
  id?: string;
  parent?: string | null;
  children?: string[];
  message?: { model?: string; inserted_at?: string; fragments?: DeepSeekFragment[] } | null;
};

export type DeepSeekConversation = {
  id?: string;
  title?: string;
  inserted_at?: string;
  updated_at?: string;
  mapping?: Record<string, DeepSeekNode>;
};

/** 我们 POST /api/conversations/import 的入参形状。
 *  注意时间是**毫秒时间戳**，后端做的是 fromtimestamp(x / 1000)，
 *  直接把 ISO 字符串塞进去会炸。 */
export type ImportSession = {
  title: string;
  createdAt?: number;
  updatedAt?: number;
  messages: { role: "user" | "assistant"; content: string }[];
};

const FRAGMENT_ROLE: Record<string, "user" | "assistant"> = {
  REQUEST: "user",
  RESPONSE: "assistant"
};

/** 去掉 PostgreSQL 存不下的字符。
 *
 *  text 列不接受 NUL（`invalid byte sequence for encoding "UTF8": 0x00`），
 *  而导出包里真的会有——用户往对话里粘终端输出时，尾部常带一个 \x00。
 *  实测 582 个对话里就有一条把整批导入打成 500（2026-08-13）。
 *
 *  落单的代理项（lone surrogate）同理：JSON 里合法，编成 UTF-8 时非法。 */
function sanitize(text: string): string {
  return text
    .replace(/\u0000/g, "")
    .replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, "");
}

/** 从 root 沿 children 走到底。
 *
 *  遇到分叉（用户点过"重新生成"）取**最后一个**——那是用户最终看到的那版。
 *  真实数据里 4600 个节点只有 24 个有分叉，取哪个影响面都 <0.5%，
 *  但总得选一个说得出理由的。
 *
 *  guard 是防环的：mapping 来自外部文件，不能假设它一定是棵合法的树，
 *  一个自指的 children 就能让这里转到浏览器卡死。 */
function walkMainPath(mapping: Record<string, DeepSeekNode>): DeepSeekNode[] {
  const out: DeepSeekNode[] = [];
  const seen = new Set<string>();
  let node: DeepSeekNode | undefined = mapping.root;
  let guard = 0;

  while (node && guard++ < 100000) {
    const kids: string[] = node.children ?? [];
    if (kids.length === 0) break;
    const nextId: string | undefined = kids[kids.length - 1];
    if (!nextId || seen.has(nextId)) break;
    seen.add(nextId);
    const next: DeepSeekNode | undefined = mapping[nextId];
    if (!next) break;
    out.push(next);
    node = next;
  }
  return out;
}

/** 一个对话 → 扁平消息列表。转不出东西就返回空数组，由调用方决定跳过。 */
export function conversationToMessages(conv: DeepSeekConversation): ImportSession["messages"] {
  const mapping = conv.mapping;
  if (!mapping) return [];

  const msgs: ImportSession["messages"] = [];
  for (const node of walkMainPath(mapping)) {
    const message = node.message;
    if (!message) continue; // root 和某些占位节点没有 message
    for (const frag of message.fragments ?? []) {
      const role = FRAGMENT_ROLE[frag.type ?? ""];
      if (!role) continue; // THINK / SEARCH / FILE 不进正文
      const content = sanitize(frag.content ?? "");
      if (!content.trim()) continue;
      // 同一条 message 里可能有多个同角色片段（流式分段），合并成一条，
      // 否则导进来会变成连续两条 assistant，看着像模型自言自语
      const last = msgs[msgs.length - 1];
      if (last && last.role === role) {
        last.content += "\n\n" + content;
      } else {
        msgs.push({ role, content });
      }
    }
  }
  return msgs;
}

/** ISO 字符串 → 毫秒时间戳。解析不了就返回 undefined，让后端用当前时间兜底，
 *  而不是塞一个 NaN 进去（NaN/1000 在后端会抛异常，整批导入都跟着挂）。 */
function toMillis(iso?: string): number | undefined {
  if (!iso) return undefined;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : undefined;
}

export type ConvertResult = {
  sessions: ImportSession[];
  /** 转出来是空的（没有任何正文片段），跳过没导入 */
  skippedEmpty: number;
  /** 转换过程抛异常的，跳过没导入 */
  failed: number;
};

/** conversations.json 的内容 → 可直接提交的 sessions。
 *
 *  单条失败不让整批泡汤：13MB 的包里有一条畸形数据就全军覆没，
 *  对用户来说是最难受的失败方式。 */
export function convertConversations(raw: unknown): ConvertResult {
  const list = Array.isArray(raw) ? (raw as DeepSeekConversation[]) : [];
  const sessions: ImportSession[] = [];
  let skippedEmpty = 0;
  let failed = 0;

  for (const conv of list) {
    try {
      const messages = conversationToMessages(conv);
      if (messages.length === 0) {
        skippedEmpty++;
        continue;
      }
      sessions.push({
        title: sanitize(conv.title || "未命名对话").slice(0, 200) || "未命名对话",
        createdAt: toMillis(conv.inserted_at),
        updatedAt: toMillis(conv.updated_at),
        messages
      });
    } catch {
      failed++;
    }
  }

  return { sessions, skippedEmpty, failed };
}

/** 从导出包的 zip 里取出 conversations.json 并解析。
 *
 *  只解这一个成员：user.json 是账号信息（手机号、邮箱、第三方 profile），
 *  我们没有任何理由读它，不读就不会不小心用上。 */
export async function readConversationsFromZip(file: File): Promise<unknown> {
  const { unzipSync, strFromU8 } = await import("fflate");
  const buf = new Uint8Array(await file.arrayBuffer());
  const entries = unzipSync(buf, {
    filter: (f) => f.name === "conversations.json" || f.name.endsWith("/conversations.json")
  });
  const name = Object.keys(entries)[0];
  if (!name) {
    throw new Error("压缩包里没有找到 conversations.json，请确认选的是 DeepSeek 导出的数据包");
  }
  return JSON.parse(strFromU8(entries[name]));
}
